import {
  type OutputTechnology as CommonsOutputTechnology,
  isOutputTechnology as isCommonsOutputTechnology,
} from '@enmon/commons/lib/tools/types';

export type OutputTechnologySelection = '' | CommonsOutputTechnology;

export interface InventorizationOutput {
  id?: number | string;
  isOccupied: boolean;
  remoteReadingOwner: string;
  technology: CommonsOutputTechnology;
}

export function mergeInbuiltOutputs(
  currentOutputs: InventorizationOutput[],
  technologies: CommonsOutputTechnology[],
): InventorizationOutput[] {
  return technologies.map(technology => {
    const existingOutput = currentOutputs.find(output => output.technology === technology);

    return (
      existingOutput ?? {
        isOccupied: false,
        remoteReadingOwner: '',
        technology,
      }
    );
  });
}

export function readInbuiltOutputTechnologies(details: unknown): CommonsOutputTechnology[] {
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return [];
  }

  const record = details as { inbuiltOutputs?: unknown[] | string };
  const rawValue = record.inbuiltOutputs;
  const values = Array.isArray(rawValue) ? rawValue : typeof rawValue === 'string' ? rawValue.split(/[;,]/) : [];

  return [...new Set(values.map(value => readOutputTechnology(value)).filter(isCommonsOutputTechnology))];
}

export function readOutputTechnology(value: unknown): OutputTechnologySelection {
  return isCommonsOutputTechnology(value) ? value : '';
}

export function readOutputs(value: unknown): InventorizationOutput[] {
  return Array.isArray(value)
    ? value.map(readOutput).filter((output): output is InventorizationOutput => Boolean(output))
    : [];
}

function readOutput(value: unknown): InventorizationOutput | null {
  const record = value as Partial<InventorizationOutput>;
  const technology = readOutputTechnology(record.technology);

  if (!isCommonsOutputTechnology(technology)) {
    return null;
  }

  return {
    id: readNumberOrString(record.id),
    isOccupied: record.isOccupied === true,
    remoteReadingOwner: record.remoteReadingOwner ?? '',
    technology,
  };
}

function readNumberOrString(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string' || !value) {
    return undefined;
  }

  return value;
}
